import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { User } from '../types/user.types';
import {
  AuthUserResponsePartner,
  IncompleteLocationEntity,
  SimpleLocationWithTodayDataResponse,
} from '../types';

type IncompleteLocation = IncompleteLocationEntity | null;

// === AUTH STATE ===

type AuthState = {
  locations: SimpleLocationWithTodayDataResponse[];
  user: User | null;
  tokens: {
    accessToken: string;
    refreshToken: string;
  } | null;
  incompleteLocation: IncompleteLocation;
  locationsAmount: number;
  verifiedLocationsAmount: number;
  mainLocation: {
    id: number;
    name: string;
  } | null;
  lastLogin: string | null;
  lastLogout: {
    date: string;
    status: number;
    error: string;
    place: string;
  } | null;
  lastLogoutLayout: {
    date: string;
    status: number;
    error: string;
    place: string;
  } | null;
  loginPhoneNumber: string | null;
  loginHasEmail: boolean;
  /** Merchant v2 sign in just created an owner account for this number. */
  loginIsNewUser: boolean;
  saveLocations: (data: SimpleLocationWithTodayDataResponse[]) => void;
  updateLastLogin: (data: string) => void;
  updateLastLogout: (data: {
    date: string;
    status: number;
    error: string;
    place: string;
  }) => void;
  updateLastLogoutLayout: (data: {
    date: string;
    status: number;
    error: string;
    place: string;
  }) => void;
  updateAuthState: (data: {
    user: User;
    tokens: {
      accessToken: string;
      refreshToken: string;
    };
  }) => void;

  updateNotificationStatus: (data: boolean) => void;
  updateEmailNotificationStatus: (data: boolean) => void;
  updatePartnerAuthState: (data: AuthUserResponsePartner) => void;
  updateUserEmailVerification: () => void;
  updateUser: (data: User) => void;
  saveIncompleteLocation: (data: IncompleteLocation) => void;
  saveLocationsAmount: (data: number) => void;
  saveLoginPhoneNumber: (data: string) => void;
  saveLoginHasEmail: (data: boolean) => void;
  saveLoginIsNewUser: (data: boolean) => void;
};

export const useAuth = create<AuthState>()(
  devtools(
    persist(
      (set) => ({
        locations: [],
        lastLogin: null,
        lastLogout: null,
        lastLogoutLayout: null,
        user: null,
        tokens: null,
        incompleteLocation: null,
        locationsAmount: 0,
        verifiedLocationsAmount: 0,
        mainLocation: null,
        loginPhoneNumber: null,
        loginHasEmail: false,
        loginIsNewUser: false,
        saveLocations: (data: SimpleLocationWithTodayDataResponse[]) =>
          set(() => ({ locations: data })),
        saveLoginPhoneNumber: (data) => set(() => ({ loginPhoneNumber: data })),
        saveLoginHasEmail: (data) => set(() => ({ loginHasEmail: data })),
        saveLoginIsNewUser: (data) => set(() => ({ loginIsNewUser: data })),
        updateLastLogin: (data: string) => set(() => ({ lastLogin: data })),
        updateLastLogout: (data: {
          date: string;
          status: number;
          error: string;
          place: string;
        }) => set(() => ({ lastLogout: data })),
        updateLastLogoutLayout: (data: {
          date: string;
          status: number;
          error: string;
          place: string;
        }) => set(() => ({ lastLogoutLayout: data })),
        updateAuthState: (data) =>
          set(() => ({ user: data.user, tokens: data.tokens })),
        //  PARTNER SIGN UP
        updatePartnerAuthState: (data: AuthUserResponsePartner) =>
          set(() => ({
            user: data.user,
            tokens: data.tokens,
            locationsAmount: data.locationsAmount,
            verifiedLocationsAmount: data.verifiedLocationsAmount,
            incompleteLocation: data.incompleteLocation,
            mainLocation: data.mainLocation,
          })),

        updateUser: (data) =>
          set(() => ({
            user: data,
          })),

        updateUserEmailVerification: () =>
          set((state) => ({
            ...state,
            user: { ...state.user, emailVerified: true } as User,
          })),

        saveIncompleteLocation: (data: IncompleteLocation) =>
          set(() => ({
            incompleteLocation: data,
          })),

        saveLocationsAmount: (data: number) =>
          set(() => ({
            locationsAmount: data,
          })),

        updateNotificationStatus: (data) =>
          set((state) => ({
            ...state,
            user: state.user
              ? { ...state.user, notificationsEnabled: data }
              : null,
          })),

        updateEmailNotificationStatus: (data) =>
          set((state) => ({
            ...state,
            user: state.user
              ? { ...state.user, emailNotificationsEnabled: data }
              : null,
          })),
      }),
      { name: 'vg-admin-auth' }
    ),
    { name: 'auth' }
  )
);

// === SIDEBAR STATE ===

type SidebarState = {
  sidebarIsOpen: boolean;
  setSidebarIsOpen: (value: boolean) => void;
};

export const useSidebarState = create<SidebarState>()(
  devtools(
    persist(
      (set) => ({
        sidebarIsOpen:
          typeof window !== 'undefined' ? window.innerWidth > 800 : true,
        setSidebarIsOpen: (value) => set(() => ({ sidebarIsOpen: value })),
      }),
      {
        name: 'vg-admin-sidebar-state',
        // If a previous session left the sidebar open, don't auto-open it on mobile.
        onRehydrateStorage: () => (state, error) => {
          if (typeof window !== 'undefined' && window.innerWidth <= 800) {
            state?.setSidebarIsOpen(false);
          }
        },
      }
    ),
    { name: 'sidebar' }
  )
);

// === UI STATE ===

type UIState = {
  verificationsTab: number;
  setVerificationsTab: (value: number) => void;
  settingsTab: number;
  setSettingsTab: (value: number) => void;
  workingHoursTab: number;
  setWorkingHoursTab: (value: number) => void;
  menuTab: number;
  setMenuTab: (value: number) => void;
  clientPaymentsTab: number;
  setClientPaymentsTab: (value: number) => void;
  providersTab: number;
  setProvidersTab: (value: number) => void;
};

export const useUIState = create<UIState>()(
  devtools(
    persist(
      (set) => ({
        verificationsTab: 1,
        setVerificationsTab: (value) =>
          set(() => ({ verificationsTab: value })),
        settingsTab: 1,
        setSettingsTab: (value) => set(() => ({ settingsTab: value })),
        workingHoursTab: 1,
        setWorkingHoursTab: (value) => set(() => ({ workingHoursTab: value })),
        menuTab: 1,
        setMenuTab: (value) => set(() => ({ menuTab: value })),
        clientPaymentsTab: 1,
        setClientPaymentsTab: (value) =>
          set(() => ({ clientPaymentsTab: value })),
        providersTab: 1,
        setProvidersTab: (value) => set(() => ({ providersTab: value })),
      }),
      { name: 'vg-admin-ui-state' }
    ),
    { name: 'ui' }
  )
);

export function useAuthenticatedAuthUser() {
  const user = useAuth((state) => state.user);
  if (!user) {
    throw new Error('User is not authenticated');
  }
  return user;
}
